import { ExternalLink } from 'lucide-react';
import type { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@nucleus-commerce/ui/components/tooltip';
import { cn } from '@nucleus-commerce/ui/lib/utils';

export interface ViewOnStorefrontButtonProps {
  /** The entity's live URL as the API resolved it; null while the store has no domain or link template. */
  url: string | null;
  /** Whether the storefront serves the entity right now (published product, active collection). */
  live: boolean;
  /** Defaults to "View on storefront". */
  label?: string;
  variant?: ComponentProps<typeof Button>['variant'];
  size?: ComponentProps<typeof Button>['size'];
  className?: string;
}

/** Opens the storefront page in a new tab, or explains why it cannot yet. */
export function ViewOnStorefrontButton({
  url,
  live,
  label,
  variant = 'outline',
  size = 'sm',
  className,
}: ViewOnStorefrontButtonProps) {
  const { t } = useTranslation('stores');
  const text = label ?? t('storefront.view');
  if (url && live) {
    return (
      <Button asChild variant={variant} size={size} className={className}>
        <a href={url} target="_blank" rel="noopener noreferrer">
          <ExternalLink aria-hidden />
          {text}
        </a>
      </Button>
    );
  }
  // Own provider: the button also renders in cards and tables outside the app shell's.
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {/* aria-disabled rather than disabled so the reason stays reachable by hover and focus. */}
          <Button
            type="button"
            variant={variant}
            size={size}
            className={cn('cursor-not-allowed opacity-50', className)}
            aria-disabled="true"
            onClick={(event) => event.preventDefault()}
          >
            <ExternalLink aria-hidden />
            {text}
          </Button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          {url ? t('storefront.notLive') : t('storefront.noUrl')}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
